import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  ApiError,
  cardIdsFromSourceRef,
  isFlowControlAction,
  type LegalAction,
  type MatchPublicView,
} from '../api-client.js';
import { useAuthStore } from '../auth/auth-store.js';
import {
  AflClearancePanel,
  AflContestPanel,
  AflFieldView,
  AflRuckPanel,
  AflScoreBoard,
  type AflViewerState,
} from '../components/afl/AflView.js';
import { AflMatch } from '../components/afl/AflMatch.js';
import { GoldenBallsMatch } from '../components/goldenballs/GoldenBallsMatch.js';
import {
  isGoldenBallsView,
  type GoldenBallsViewerState,
} from '../components/goldenballs/types.js';
import { HandCard, type HandCardData } from '../components/HandCard.js';
import { CardPreviewModal } from '../components/CardPreviewModal.js';
import { CardActionPopover } from '../components/CardActionPopover.js';
import { EventLog } from '../components/EventLog.js';
import { MatchClock } from '../components/MatchClock.js';
import { useTurnNotifications } from '../notifications/use-turn-notifications.js';
import { useEventStream } from '../streams/use-event-stream.js';
import { useUserDisplayName } from '../users/use-user-profile.js';
import { listDecks } from '../decks/deck-storage.js';
import { validateForGame } from '../decks/formats.js';
import type { DeckRecord } from '../decks/types.js';
import { buildSeatLabels, humanizeSeatRefs } from '../lib/seat-labels.js';

// ---------------------------------------------------------------------------
// Viewer-state types (sport-specific; shared hand shape)
// ---------------------------------------------------------------------------

type CricketInnings = { runs: number; wickets: number; balls: number };

type CricketTeamView = {
  innings: CricketInnings[];
  total: number;
  handCount: number;
  deckCount: number;
  discardCount: number;
  hand: HandCardData[];
};

type CricketViewerState = {
  phase: string;
  battingSeatId: string | null;
  bowlingSeatId: string | null;
  currentInnings: number;
  lastEventSummary: string;
  winnerSeatId: string | null;
  completedReason: string | null;
  pendingBowl: { bowler?: string; delivery?: string } | null;
  teams: Record<string, CricketTeamView>;
};

type ViewerState = AflViewerState | CricketViewerState | GoldenBallsViewerState;

// Fallback poll cadence — used while SSE is disconnected (network
// blip, intermediate proxy timeout). When the stream is healthy
// pushes are immediate so the interval is mostly idle work.
const POLL_MS = 10_000;

// Golden Balls carries an explicit gameKey (see its projection), so the
// guard is a tag check rather than shape-sniffing.
function isAflView(view: ViewerState): view is AflViewerState {
  return 'quarter' in view && 'teams' in view && typeof (view as { quarter?: unknown }).quarter === 'number';
}
function isCricketView(view: ViewerState): view is CricketViewerState {
  return 'battingSeatId' in view;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function MatchPage() {
  const { matchId = '' } = useParams();
  const user = useAuthStore((s) => s.user);

  const [match, setMatch] = useState<MatchPublicView | null>(null);
  const [view, setView] = useState<ViewerState | null>(null);
  const [legalActions, setLegalActions] = useState<LegalAction[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!matchId) return;
    try {
      // Spectator path (no auth, or signed in but not a participant
      // of this match): use the public viewer-state endpoint and
      // skip legal-actions entirely. Player path: per-user viewer
      // state + legal actions for the action bar.
      if (!user) {
        const [m, v] = await Promise.all([
          api.getMatch(matchId),
          api.getPublicViewerState<ViewerState>(matchId),
        ]);
        setMatch(m);
        setView(v);
        setLegalActions([]);
        return;
      }
      const [m, v, acts] = await Promise.all([
        // Pass our own id so hidden-info redaction leaves OUR participant
        // row identifiable — otherwise a seated player (creator
        // included) can't tell they're in their own match.
        api.getMatch(matchId, user.id),
        api.getViewerState<ViewerState>(matchId, user.id),
        api.getLegalActions(matchId, user.id),
      ]);
      setMatch(m);
      setView(v);
      setLegalActions(acts);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : String(error);
      setError(message);
    }
  }, [matchId, user]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // Live updates via SSE — every push from the server triggers an
  // immediate refetch. Polling above is the safety net for when the
  // stream is between reconnects.
  useEventStream(matchId ? `/api/streams/matches/${matchId}` : null, () => {
    refresh();
  });

  // Derived view of the user's relationship to the match. Computed
  // even when match is null so the hook below this can run on every
  // render (rules-of-hooks: never call a hook after a conditional
  // early return).
  const isParticipant = !!user && !!match && match.participants.some((p) => p.userId === user.id);
  const isSpectator = !isParticipant;
  const canJoin =
    !!user && !!match && match.match.status === 'waiting_for_players' && !isParticipant;
  const userSeatId = user && match ? findSeatId(match, user.id) : null;

  // Browser notification when the opponent moves and it becomes
  // our turn (only fires when the tab is hidden, only for human
  // opponents, only if the user has opted in). The hook handles
  // null match / null userSeatId gracefully — see
  // notifications/use-turn-notifications.ts.
  useTurnNotifications({ match, userSeatId, enabled: isParticipant });

  // Hook must run on every render — never put a hook after an early
  // return. The empty-map fallback covers the pre-load render.
  const seatLabels = useMemo(
    () => (match ? buildSeatLabels(match, userSeatId) : {}),
    [match, userSeatId],
  );

  if (!match || !view) {
    return <p className="text-ec-bone">Loading match…</p>;
  }

  // Golden Balls owns its own screens end to end — the generic flow
  // below reads AFL/cricket-shaped viewer state, so this branch must
  // cover the waiting lobby as well as live play.
  if (isGoldenBallsView(view) && match.match.status === 'waiting_for_players') {
    const seatsFilled = match.participants.length;
    const seatsTotal = match.seats.length;
    return (
      <div className="mx-auto flex max-w-md flex-col gap-4 py-6 text-center">
        <h2 className="font-display text-xl font-semibold uppercase tracking-wide text-ec-face">
          Golden Balls
        </h2>
        {/* Say what is MISSING, not just a fraction. "2 of 4 seats
            taken" is accurate and tells you nothing about why the game
            has not started or what to do about it. */}
        <p className="font-serif text-sm text-ec-bone">
          {seatsTotal - seatsFilled === 0
            ? 'All four seats taken — starting…'
            : `Waiting on ${seatsTotal - seatsFilled} more ${
                seatsTotal - seatsFilled === 1 ? 'player' : 'players'
              }. Golden Balls needs all ${seatsTotal} seats before it can start.`}
        </p>
        <p className="font-serif text-xs text-ec-bone-dim">
          {seatsFilled} of {seatsTotal} seated · two rounds of lying, then Split or
          Steal. Play money only.
        </p>
        {error && (
          <p role="alert" className="font-serif text-xs text-ec-oxblood-2">
            {error}
          </p>
        )}
        {canJoin && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onJoin()}
            className="ec-btn ec-btn--primary min-h-[3rem] disabled:opacity-40"
          >
            Take a seat
          </button>
        )}
        {isParticipant && match.match.createdByUserId === user?.id && (
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.fillBotSeats(matchId, user!.id);
                await refresh();
              } catch (error) {
                setError(error instanceof ApiError ? error.message : String(error));
              } finally {
                setBusy(false);
              }
            }}
            className="ec-btn min-h-[3rem] disabled:opacity-40"
          >
            Start now with bots
          </button>
        )}
        {isSpectator && !canJoin && (
          <p className="font-serif text-xs text-ec-bone-dim">Log in to take a seat.</p>
        )}
      </div>
    );
  }

  if (isGoldenBallsView(view)) {
    return (
      <GoldenBallsMatch
        view={view}
        match={match}
        legalActions={legalActions}
        busy={busy}
        error={error}
        onAct={async ({ actionKind, descriptorId, choices }) => {
          setBusy(true);
          setError(null);
          try {
            await api.submitAction(matchId, {
              userId: user!.id,
              actionKind,
              descriptorId,
              choices: choices ?? {},
            });
            await refresh();
          } catch (error) {
            setError(error instanceof ApiError ? error.message : String(error));
          } finally {
            setBusy(false);
          }
        }}
      />
    );
  }

  async function act(a: LegalAction, payIds?: string[]) {
    setBusy(true);
    setError(null);
    try {
      await api.submitAction(matchId, {
        userId: user!.id,
        actionKind: a.actionKind,
        descriptorId: a.descriptorId,
        // The recycle-cost picker sends the exact cards to pay with; empty
        // otherwise (free plays, or a forced pay the engine auto-resolves).
        choices: payIds && payIds.length > 0 ? { payIds } : {},
      });
      await refresh();
    } catch (error) {
      setError(error instanceof ApiError ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function onJoin(deck?: DeckRecord) {
    setBusy(true);
    setError(null);
    try {
      const deckPayload = deck
        ? { formatKey: deck.formatKey, entries: deck.entries }
        : undefined;
      await api.joinMatch(matchId, user!.id, deckPayload);
      await refresh();
    } catch (error) {
      setError(error instanceof ApiError ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function onCancel() {
    if (!confirm('Cancel this match? The lobby entry will be deleted.')) return;
    setBusy(true);
    setError(null);
    try {
      await api.cancelMatch(matchId, user!.id);
      // Match is gone — bounce back to the matches list. A
      // refresh would 404 on the deleted record.
      window.location.assign('/me/matches');
    } catch (error) {
      setError(error instanceof ApiError ? error.message : String(error));
      setBusy(false);
    }
  }

  const winnerSeatId = view.winnerSeatId;
  const winnerLabel = winnerSeatId ? (seatLabels[winnerSeatId] ?? winnerSeatId) : null;
  const completedReason = view.completedReason;
  const lastEventSummary = humanizeSeatRefs(view.lastEventSummary, seatLabels);
  const userHand = userSeatId ? (view.teams[userSeatId]?.hand ?? []) : [];

  // AFL gameplay gets the dedicated viewport-fit, click-to-move layout.
  // The pre-game "waiting for players" screen keeps the simple scrollable
  // flow below (join / share panel), so only in-progress + completed
  // matches route into AflMatch.
  if (isAflView(view) && match.match.status !== 'waiting_for_players') {
    return (
      <AflMatch
        view={view}
        match={match}
        userSeatId={userSeatId}
        isParticipant={isParticipant}
        legalActions={legalActions}
        busy={busy}
        onAct={act}
        matchId={matchId}
        seatLabels={seatLabels}
        lastEventSummary={lastEventSummary}
        error={error}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/*
        Header layout breaks up into two visual groups:
          - Title + match-id + status (left, identifies the match)
          - Clock + spectator pill + replay link + resign (right,
            ml-auto so they stay together on the right edge even
            when the title group wraps on a phone)
        Both groups wrap internally so nothing ever overflows.
      */}
      <header className="flex flex-wrap items-baseline gap-x-4 gap-y-2 sm:gap-x-6">
        <h2 className="font-display text-lg font-semibold uppercase tracking-wide text-ec-face">{match.game.name}</h2>
        <span className="hidden truncate font-mono text-xs text-ec-bone-dim sm:inline">
          {match.match.id}
        </span>
        <span className="text-xs text-ec-bone sm:text-sm">
          <span className="text-ec-bone-dim">Status:</span>{' '}
          <strong className="text-ec-face">{match.match.status}</strong>
        </span>
        <div className="ml-auto flex flex-wrap items-baseline gap-x-3 gap-y-2">
        {match.match.status === 'in_progress' && match.decisionWindow?.deadlineAt && (
          <ClockBadge
            deadlineAt={match.decisionWindow.deadlineAt}
            youAreActive={
              !!userSeatId && match.decisionWindow.activeSeatId === userSeatId
            }
          />
        )}
        {isSpectator && match.match.status !== 'waiting_for_players' && (
          <span className="rounded-full border border-ec-ruck/60 bg-[rgba(62,68,77,0.14)] px-2 py-0.5 font-display text-[10px] font-semibold uppercase tracking-wider text-ec-bone">
            Spectating
          </span>
        )}
        {(match.match.status === 'completed' || match.match.status === 'in_progress') && (
          <Link
            to={`/matches/${matchId}/replay`}
            className="ec-btn !py-1 text-xs"
          >
            Replay events →
          </Link>
        )}
        {match.match.status === 'in_progress' && isParticipant && (
          <ResignButton legalActions={legalActions} busy={busy} onAct={act} />
        )}
        </div>
      </header>

      {isSpectator && !user && (
        <div className="rounded-md border border-ec-ruck/60 bg-[rgba(62,68,77,0.14)] p-3 font-serif text-sm text-ec-bone">
          You're watching this match as a guest.{' '}
          <Link to="/login" className="underline hover:text-ec-face">
            Log in
          </Link>{' '}
          to play your own.
        </div>
      )}

      {match.match.status === 'waiting_for_players' && (
        <div className="rounded-md border border-ec-brass/60 bg-[rgba(245,168,28,0.18)] p-4">
          <p className="font-serif text-sm text-ec-brass">
            Waiting for players. Share this URL with an opponent: open a second
            browser (or incognito), register another account, then paste the
            current URL.
          </p>
          <p className="mt-2 text-xs text-ec-brass">
            Participants: {match.participants.length} / {match.seats.length}
          </p>
          {match.participants.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-xs text-ec-brass">
              {match.participants.map((p) => (
                <ParticipantLine
                  key={p.id}
                  userId={p.userId}
                  isYou={!!user && p.userId === user.id}
                />
              ))}
            </ul>
          )}
          {canJoin && (
            <JoinPanel
              gameKey={match.match.gameKey}
              busy={busy}
              onJoin={onJoin}
            />
          )}
          {!!user && match.match.createdByUserId === user.id && (
            <div className="mt-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="rounded-md border border-ec-oxblood bg-[rgba(39,147,211,0.28)] px-3 py-1.5 font-display text-xs uppercase tracking-wide text-ec-face hover:border-ec-oxblood-2 disabled:opacity-50"
                title="Delete this match — only works while no one else has joined"
              >
                Cancel match
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-ec-oxblood bg-[rgba(39,147,211,0.22)] p-3 text-sm text-ec-face">
          {error}
        </div>
      )}

      {isAflView(view) ? (
        <div className="space-y-4">
        {/*
          On mobile the source order is the visual order: field first
          (the focal point), then scoreboard / contest / events, then
          hand + action buttons (which sit close to the thumbs).
          Desktop reshuffles to a 3-column layout via lg:order.
        */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_2fr_1fr] lg:gap-6">
          {/* Field — first on mobile, centre on desktop */}
          <div className="min-w-0 lg:order-2">
            <AflFieldView view={view} match={match} userSeatId={userSeatId} />
          </div>

          {/* Scoreboard / contest / events — second on mobile, right on desktop */}
          <div className="min-w-0 space-y-4 lg:order-3">
            <AflScoreBoard view={view} match={match} userSeatId={userSeatId} />
            {view.ruck && (
              <AflRuckPanel ruck={view.ruck} match={match} userSeatId={userSeatId} />
            )}
            {view.clearance && (
              <AflClearancePanel
                clearance={view.clearance}
                match={match}
                userSeatId={userSeatId}
              />
            )}
            {view.contest && (
              <AflContestPanel contest={view.contest} userSeatId={userSeatId} />
            )}
            <section className="ec-surface p-4">
              <h3 className="mb-2 font-display text-sm font-medium uppercase tracking-wide text-ec-bone">Event summary</h3>
              <p className="font-serif text-sm text-ec-face">{lastEventSummary}</p>
            </section>
            <EventLog matchId={matchId} seatLabels={seatLabels} />
            {winnerSeatId && (
              <div className="rounded-md border border-ec-defence bg-[rgba(85,167,47,0.18)] p-4 text-ec-face">
                <strong>Match over.</strong> Winner: {winnerLabel} ({completedReason}).
              </div>
            )}
          </div>

          {/* Hand + actions — last on mobile, left on desktop */}
          <div className="min-w-0 space-y-4 lg:order-1">
            <Hand
              hand={userHand}
              legalActions={legalActions}
              busy={busy}
              onAct={act}
            />
            <ActionBar
              legalActions={legalActions}
              busy={busy}
              onAct={act}
              isParticipant={isParticipant}
            />
          </div>
        </div>
        </div>
      ) : (
        <>
          {isCricketView(view) && (
            <CricketView view={view} match={match} userSeatId={userSeatId} />
          )}

          <section className="ec-surface p-4">
            <h3 className="mb-2 font-display text-sm font-medium uppercase tracking-wide text-ec-bone">Event summary</h3>
            <p className="font-serif text-sm text-ec-face">{lastEventSummary}</p>
          </section>

          <EventLog matchId={matchId} seatLabels={seatLabels} />

          <Hand
            hand={userHand}
            legalActions={legalActions}
            busy={busy}
            onAct={act}
          />

          <ActionBar
            legalActions={legalActions}
            busy={busy}
            onAct={act}
            isParticipant={isParticipant}
          />

          {winnerSeatId && (
            <div className="rounded-md border border-ec-defence bg-[rgba(85,167,47,0.18)] p-4 text-ec-face">
              <strong>Match over.</strong> Winner: {winnerSeatId} ({completedReason}).
            </div>
          )}
        </>
      )}

      <Link to="/" className="inline-block font-display text-sm uppercase tracking-wide text-ec-brass hover:underline">
        ← back to games
      </Link>
    </div>
  );
}

function findSeatId(match: MatchPublicView, userId: string): string | null {
  const p = match.participants.find((x) => x.userId === userId);
  return p?.seatId ?? null;
}

// ---------------------------------------------------------------------------
// Cricket panels
// ---------------------------------------------------------------------------

function CricketView({
  view,
  match,
  userSeatId,
}: {
  view: CricketViewerState;
  match: MatchPublicView;
  userSeatId: string | null;
}) {
  return (
    <>
      <CricketScoreBoard view={view} match={match} userSeatId={userSeatId} />
      {view.pendingBowl && <CricketPendingBowlPanel pending={view.pendingBowl} />}
    </>
  );
}

function CricketScoreBoard({
  view,
  match,
  userSeatId,
}: {
  view: CricketViewerState;
  match: MatchPublicView;
  userSeatId: string | null;
}) {
  const seatIds = Object.keys(view.teams);
  return (
    <section className="ec-surface p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-display text-sm font-medium uppercase tracking-wide text-ec-bone">Score</h3>
        <p className="text-xs text-ec-bone-dim">
          Innings {view.currentInnings + 1} · phase{' '}
          <strong className="text-ec-bone">{view.phase}</strong>
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {seatIds.map((seatId) => {
          const team = view.teams[seatId]!;
          const seat = match.seats.find((s) => s.id === seatId);
          const isYou = seatId === userSeatId;
          const isBatting = seatId === view.battingSeatId;
          const isBowling = seatId === view.bowlingSeatId;
          const current = team.innings[team.innings.length - 1];
          return (
            <div
              key={seatId}
              className={`rounded-md border p-3 ${
                isYou ? 'border-ec-ruck/70 bg-[rgba(62,68,77,0.16)]' : 'border-ec-line bg-ec-ground'
              }`}
            >
              <p className="font-display text-sm font-medium uppercase tracking-wide text-ec-face">
                {seat?.seatKey ?? seatId.slice(0, 8)}
                {isYou && ' (you)'}
                {isBatting && ' · batting'}
                {isBowling && ' · bowling'}
              </p>
              <p className="mt-2 font-display text-3xl font-semibold tabular-nums text-ec-face">
                {team.total}
                {current && (
                  <span className="ml-2 text-xs font-normal text-ec-bone-dim">
                    current: {current.runs}/{current.wickets}
                    {' '}({current.balls} balls)
                  </span>
                )}
              </p>
              <p className="mt-1 text-xs text-ec-bone-dim">
                hand {team.handCount} · deck {team.deckCount} · discard{' '}
                {team.discardCount}
              </p>
              {team.innings.length > 1 && (
                <p className="mt-1 text-xs text-ec-bone-dim">
                  first innings: {team.innings[0]!.runs}/{team.innings[0]!.wickets}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CricketPendingBowlPanel({
  pending,
}: {
  pending: NonNullable<CricketViewerState['pendingBowl']>;
}) {
  return (
    <section className="rounded-lg border border-ec-brass/60 bg-[rgba(245,168,28,0.18)] p-3 text-sm">
      <p className="font-serif text-ec-brass">
        Pending delivery: <strong>{pending.bowler ?? 'Bowler'}</strong> bowls{' '}
        <strong>{pending.delivery ?? 'a ball'}</strong>. Batter to respond.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared UI
// ---------------------------------------------------------------------------

type HandProps = {
  hand: HandCardData[];
  legalActions: LegalAction[];
  busy: boolean;
  onAct: (a: LegalAction) => void;
};

function Hand({ hand, legalActions, busy, onAct }: HandProps) {
  const [preview, setPreview] = useState<HandCardData | null>(null);
  const [popover, setPopover] = useState<{
    card: HandCardData;
    actions: LegalAction[];
  } | null>(null);

  // Map every card-driven legal action to the cards it references.
  // Stadium / weather / hidden cards never appear here, so they fall
  // through to the preview modal on click.
  const actionsByCardId = useMemo(() => {
    const map = new Map<string, LegalAction[]>();
    for (const action of legalActions) {
      for (const cardId of cardIdsFromSourceRef(action)) {
        const list = map.get(cardId) ?? [];
        list.push(action);
        map.set(cardId, list);
      }
    }
    return map;
  }, [legalActions]);

  const submit = (action: LegalAction) => {
    if (busy) return; // Belt-and-braces; the click site already gates this.
    setPopover(null);
    onAct(action);
  };

  return (
    <section className="ec-surface p-4">
      <h3 className="mb-2 font-display text-sm font-medium uppercase tracking-wide text-ec-bone">Your hand</h3>
      {hand.length === 0 ? (
        <p className="text-sm text-ec-bone-dim">Empty.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {hand.map((card, i) => {
            const matching = card.id ? actionsByCardId.get(card.id) ?? [] : [];
            return (
              <HandCard
                key={card.id ?? i}
                card={card}
                playable={matching.length > 0 && !busy}
                onClick={() => {
                  // Block playable clicks while a submit is in flight
                  // — rapid double-clicks otherwise fire two
                  // submitAction requests and produce racey "invalid
                  // action" responses. Preview-only clicks (no
                  // matching legal action) stay live so users can
                  // still inspect cards mid-turn.
                  if (matching.length === 0) {
                    setPreview(card);
                    return;
                  }
                  if (busy) return;
                  if (matching.length === 1) {
                    submit(matching[0]!);
                  } else {
                    setPopover({ card, actions: matching });
                  }
                }}
              />
            );
          })}
        </div>
      )}
      {preview && (
        <CardPreviewModal card={preview} onClose={() => setPreview(null)} />
      )}
      {popover && (
        <CardActionPopover
          card={popover.card}
          actions={popover.actions}
          busy={busy}
          onPick={submit}
          onPreview={() => {
            setPreview(popover.card);
            setPopover(null);
          }}
          onClose={() => setPopover(null)}
        />
      )}
    </section>
  );
}

function ActionBar({
  legalActions,
  busy,
  onAct,
  isParticipant,
}: {
  legalActions: LegalAction[];
  busy: boolean;
  onAct: (a: LegalAction) => void;
  isParticipant: boolean;
}) {
  // Only show flow-control descriptors here (pass / concede /
  // escalate-decline). Card-driven plays surface from the hand.
  const flowActions = legalActions.filter(isFlowControlAction);

  if (!isParticipant) {
    return (
      <p className="text-sm text-ec-bone-dim">Spectating — only participants can act.</p>
    );
  }
  if (legalActions.length === 0) {
    return (
      <p className="text-sm text-ec-bone-dim">
        Not your turn. The page refreshes every 2 seconds.
      </p>
    );
  }
  if (flowActions.length === 0) {
    return (
      <p className="text-sm text-ec-bone-dim">
        Your turn — click a glowing card to play it.
      </p>
    );
  }
  return (
    <section>
      <h3 className="mb-2 font-display text-sm font-medium uppercase tracking-wide text-ec-bone">Other options</h3>
      <div className="flex flex-wrap gap-2">
        {flowActions.map((a) => (
          <button
            key={a.descriptorId}
            type="button"
            onClick={() => onAct(a)}
            disabled={busy}
            className="ec-btn !py-1.5 text-sm"
          >
            {a.label}
          </button>
        ))}
      </div>
    </section>
  );
}

function ParticipantLine({ userId, isYou }: { userId: string; isYou: boolean }) {
  const name = useUserDisplayName(userId);
  return (
    <li>
      · <span className="text-ec-brass">{name}</span>
      {isYou && <span className="text-ec-brass/70"> (you)</span>}
    </li>
  );
}

function ClockBadge({
  deadlineAt,
  youAreActive,
}: {
  deadlineAt: string;
  youAreActive: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1 text-sm text-ec-bone">
      {youAreActive ? '⏱ Your turn:' : '⏱ Opponent:'}{' '}
      <MatchClock deadlineAt={deadlineAt} />
    </span>
  );
}

/**
 * Top-of-page resign action. The engine surfaces `concede` as a
 * legal flow-control descriptor whenever it's available — we just
 * elevate it from "Other options" to the header so a player who
 * wants out can find it without scrolling. Falls back to disabled
 * when the engine doesn't currently allow it (e.g. mid-resolution).
 */
function ResignButton({
  legalActions,
  busy,
  onAct,
}: {
  legalActions: LegalAction[];
  busy: boolean;
  onAct: (a: LegalAction) => void;
}) {
  const concede = legalActions.find((a) => a.actionKind === 'concede');
  const [confirming, setConfirming] = useState(false);

  if (!concede) {
    return (
      <span
        className="ml-auto rounded-md border border-ec-line bg-ec-ground px-2 py-1 font-display text-xs uppercase tracking-wide text-ec-bone-dim"
        title="Resign is only available when it's your turn"
      >
        Resign (n/a)
      </span>
    );
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={busy}
        className="ml-auto rounded-md border border-ec-oxblood bg-[rgba(39,147,211,0.28)] px-2 py-1 font-display text-xs uppercase tracking-wide text-ec-face hover:border-ec-oxblood-2 hover:bg-[rgba(39,147,211,0.4)] disabled:opacity-50"
      >
        Resign
      </button>
    );
  }

  return (
    <span className="ml-auto inline-flex items-center gap-2">
      <span className="text-xs text-ec-oxblood-2">Are you sure?</span>
      <button
        type="button"
        onClick={() => onAct(concede)}
        disabled={busy}
        className="rounded-md border border-ec-oxblood bg-ec-oxblood px-2 py-1 font-display text-xs font-medium uppercase tracking-wide text-ec-face hover:bg-ec-oxblood-2 disabled:opacity-50"
      >
        Yes, resign
      </button>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        disabled={busy}
        className="rounded-md border border-ec-line px-2 py-1 font-display text-xs uppercase tracking-wide text-ec-bone hover:border-ec-brass disabled:opacity-50"
      >
        Cancel
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Join panel (deck picker + join button)
// ---------------------------------------------------------------------------

function JoinPanel({
  gameKey,
  busy,
  onJoin,
}: {
  gameKey: string;
  busy: boolean;
  onJoin: (deck?: DeckRecord) => void;
}) {
  // listDecks is async (server-backed since #27). Fetch on mount;
  // re-validate locally for the dropdown badge.
  const [decks, setDecks] = useState<DeckRecord[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    listDecks(gameKey)
      .then((d) => {
        if (!alive) return;
        setDecks(d);
        setLoaded(true);
      })
      .catch(() => {
        if (!alive) return;
        setDecks([]);
        setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [gameKey]);

  const checked = useMemo(
    () =>
      decks.map((deck) => {
        try {
          // Server-side will re-validate; client-side check is just
          // for the dropdown badge. Pool isn't loaded here, so we
          // pass an empty array — the validator's deck-size /
          // family checks still fire usefully.
          const result = validateForGame(gameKey, deck.entries, []);
          return { deck, ok: result.ok };
        } catch {
          return { deck, ok: false };
        }
      }),
    [decks, gameKey],
  );

  const firstLegal = checked.find((c) => c.ok)?.deck;
  const [selectedId, setSelectedId] = useState<string | 'none'>('none');

  // Pre-select the most recently updated legal deck once decks load.
  useEffect(() => {
    if (loaded && firstLegal && selectedId === 'none') {
      setSelectedId(firstLegal.id);
    }
  }, [loaded, firstLegal, selectedId]);

  const selected =
    selectedId === 'none' ? undefined : decks.find((d) => d.id === selectedId);

  return (
    <div className="mt-3 space-y-2">
      {decks.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-ec-brass">
            Bring a deck:
          </label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={busy}
            className="rounded-md border border-ec-brass/60 bg-[rgba(245,168,28,0.18)] px-2 py-1 text-sm text-ec-brass"
          >
            <option value="none">No deck — use the canonical starting deck</option>
            {checked.map(({ deck, ok }) => (
              <option key={deck.id} value={deck.id}>
                {deck.name} {ok ? '✓' : '(may be illegal)'}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onJoin(selected)}
          disabled={busy}
          className="ec-btn ec-btn--primary !py-1.5 text-sm"
        >
          Join this match
        </button>
        {loaded && decks.length === 0 && (
          <span className="text-xs text-ec-brass/80">
            (Build a deck under <code className="font-mono">/decks/{gameKey}</code> to bring your own.)
          </span>
        )}
      </div>
    </div>
  );
}
